// Start-up and routing. `/` is the dashboard (the public landing page when signed out), `/signin`
// signs in, `/account` holds account-wide actions, `/invite/<token>` and `/confirm-email/<token>`
// handle email links, `/s/<showId>/` opens one show in the editor.
// A show opens from the server when reachable, else from this account's prepared offline copy.

import { render } from "preact";
import { AuthError, type Account, ConnectionError, fetchAccount, rememberedAccount, signInUrl } from "./account.ts";
import { mirrorDrafts, moveMark, openEdit, openNew, resizeMark, restoreDraft } from "./actions.ts";
import { openShow } from "./idb.ts";
import { pdfUrl, watchPdfSource } from "./offline.ts";
import { canEdit, environment, notify, panel, pending, scopeSettings, show, view } from "./state.ts";
import "./styles.css";
import { SIGNED_OUT } from "./local.ts";
import { kick, listen, loadPending, sendPresence, start, wrongAccount } from "./sync.ts";
import { App } from "./ui/App.tsx";
import { AccountPage, ConfirmEmail, Dashboard, Invitation, SignIn } from "./ui/Home.tsx";
import { Landing, StartupLoading } from "./ui/Landing.tsx";
import { Privacy, Terms } from "./ui/Legal.tsx";
import { currentPage, openViewer } from "./viewer.ts";
import { scriptLoad } from "./loading.ts";
import { OpeningShow } from "./ui/ScriptLoading.tsx";

const root = document.getElementById("app") as HTMLElement;

function fatal(message: string, action?: { label: string; href: string }): void {
  document.body.classList.add("page-mode");
  root.innerHTML = "";
  const box = document.createElement("div");
  box.className = "fatal";
  const p = document.createElement("p");
  p.textContent = message;
  box.append(p);
  if (action) {
    const a = document.createElement("a");
    a.href = action.href;
    a.textContent = action.label;
    a.className = "button primary";
    box.append(a);
  }
  const home = document.createElement("a");
  home.href = "/";
  home.textContent = "All shows";
  home.className = "button";
  box.append(home);
  root.append(box);
}

/** The signed-in account, or the remembered one when q2cue can't be reached (offline reopening). */
async function account(signal?: AbortSignal): Promise<{ account: Account; online: boolean } | null> {
  try {
    const me = await fetchAccount(signal);
    environment.value = me.environment;
    return { account: me.user, online: true };
  } catch (e) {
    if (e instanceof AuthError) return null;
    if (signal && !(e instanceof ConnectionError)) throw e;
    const remembered = rememberedAccount();
    if (!remembered && signal) throw e;
    return remembered ? { account: remembered, online: false } : null;
  }
}

async function openShowPage(showId: string): Promise<void> {
  // Bound the whole account/show network handshake, including JSON response bodies.
  // A Wi-Fi connection can remain "online" while requests never settle.
  const signal = AbortSignal.timeout(10_000);
  let who;
  try {
    who = await account(signal);
  } catch (error) {
    const message = error instanceof ConnectionError ? "q2cue isn't responding. Reconnect and try again." : `This show couldn't be opened (${(error as Error).message}).`;
    return fatal(`${message} Nothing stored on this device has been erased.`, { label: "Try again", href: location.pathname });
  }
  if (!who) {
    location.replace(signInUrl());
    return;
  }
  render(<OpeningShow />, root);
  const accountId = who.account.id;
  // Another tab signed this account out (or erased it): stop at once; the queue is in IndexedDB.
  addEventListener("storage", (e) => {
    if (e.key !== SIGNED_OUT || !e.newValue) return;
    try {
      if ((JSON.parse(e.newValue) as { accountId: string }).accountId === accountId) wrongAccount();
    } catch {
      // Ignore values not written by q2cue.
    }
  });
  openShow(accountId, showId);
  scopeSettings(accountId, showId);
  let opened;
  try {
    opened = await start(accountId, showId, signal);
  } catch (e) {
    if (e instanceof AuthError) return void location.replace(signInUrl());
    if (e instanceof ConnectionError) {
      return fatal("q2cue isn't responding, and this show couldn't be opened from this browser's saved copy. Reconnect and try again. Nothing stored on this device has been erased.", { label: "Try again", href: location.pathname });
    }
    const message = (e as Error).message;
    return fatal(
      /not found/i.test(message)
        ? "This show doesn't exist or you don't have access to it. If you had unsaved changes here, find them on the shows page."
        : `This show can't be opened (${message}). If you prepared it for offline use with this account, reconnect and reload.`,
    );
  }
  const replay = await loadPending().catch((e: Error) => {
    notify(`This device's storage isn't available (${e.message}). Edits are kept in this tab only until saved.`, "warn");
    return 0;
  });
  // The HTML starts outside the editor; reveal it only after account and show access resolve.
  document.body.classList.remove("page-mode");
  render(<App />, root);
  const identity = { accountId, showId, sourceSha256: opened.snapshot.sourceSha256 };
  watchPdfSource(identity, opened.offline);
  if (replay) notify(`${replay} change${replay === 1 ? "" : "s"} restored from this device; they will be sent when connected.`);
  if (pending.value.some((e) => e.held)) notify("Changes kept from before you signed out are waiting for your review in Save status.", "warn", undefined, 0);
  await restoreDraft();
  mirrorDrafts();
  listen();
  kick();
  // Presence: which page this person is on and whether they're editing something.
  let last = "";
  setInterval(() => {
    const p = panel.value;
    const now = `${currentPage.value}|${p ? `${p.kind} ${p.mode}` : ""}`;
    if (now !== last) {
      last = now;
      sendPresence(currentPage.value, p ? (p.mode === "edit" ? `Editing a ${p.kind}` : `Adding a ${p.kind}`) : null);
    }
  }, 1000);
  await openViewer(pdfUrl(identity), {
    onNew: (anchor) => openNew(anchor),
    onEdit: (ref) => {
      if (panel.value) return;
      openEdit(ref);
    },
    onMove: moveMark,
    onResize: resizeMark,
    // In preview the page shows the export's layout, so the pen doesn't mark.
    canEdit: () => scriptLoad.value.stage === "ready" && canEdit.value && !show.value.lock && !view.value.preview && !panel.value,
  });
}

async function main(): Promise<void> {
  render(<StartupLoading signInHref={signInUrl()} />, root);
  const path = location.pathname;
  const showPath = /^\/s\/(sh_[0-9a-z]{24})\/?$/.exec(path);
  if (showPath) return openShowPage(showPath[1] as string);
  if (path === "/signin") return render(<SignIn />, root);
  if (path === "/privacy") return render(<Privacy />, root);
  if (path === "/terms") return render(<Terms />, root);
  const who = await account();
  if (!who) return path === "/" ? render(<Landing />, root) : void location.replace(signInUrl());
  if (path === "/account") return render(<AccountPage account={who.account} online={who.online} />, root);
  const invite = /^\/invite\/([A-Za-z0-9_-]{20,})$/.exec(path);
  if (invite) return render(<Invitation account={who.account} token={invite[1] as string} />, root);
  const confirm = /^\/confirm-email\/([A-Za-z0-9_-]{20,})$/.exec(path);
  if (confirm) return render(<ConfirmEmail token={confirm[1] as string} />, root);
  render(<Dashboard account={who.account} online={who.online} />, root);
}

void main();
